"use client";

import { AlertCircle, BadgeCheck, CheckCircle2, Clock, Flame, Phone, PhoneCall, Share2, ShieldCheck, Store, Truck } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ProductActions } from "@/components/product/product-actions";
import { ProductGallery } from "@/components/product/product-gallery";
import { Badge } from "@/components/ui/badge";
import { PrimeBadge } from "@/components/ui/prime-badge";
import { cn, formatPrice } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";
import type { Product, ProductVariant } from "@/types";

export function ProductDetailView({ product }: { product: Product }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const variants = useMemo(
    () => (product.variants && product.variants.length > 0 ? product.variants.filter((v) => v.isActive !== false) : []),
    [product.variants]
  );

  const initialVariant = useMemo(() => {
    if (!variants.length) return null;
    const query = (
      searchParams.get("variant") ||
      searchParams.get("color") ||
      searchParams.get("sku") ||
      ""
    ).toLowerCase().trim();

    if (query) {
      const found = variants.find(
        (v) =>
          v.id.toLowerCase() === query ||
          (v.sku && v.sku.toLowerCase() === query) ||
          (v.color && v.color.toLowerCase() === query) ||
          (v.title && v.title.toLowerCase() === query)
      );
      if (found) return found;
    }
    return variants[0];
  }, [variants, searchParams]);

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(initialVariant);
  const [hoveredVariant, setHoveredVariant] = useState<ProductVariant | null>(null);
  const [swatchErrors, setSwatchErrors] = useState<Record<string, boolean>>({});

  const isVendorProduct = Boolean(product.vendorId);
  const isCustomerListing =
    !isVendorProduct &&
    Boolean(
      product.tags?.some((t) => t.toLowerCase() === "marketplace") ||
      (product.userId && !product.vendorId && product.sellerPhone)
    );

  const SIZE_ORDER = useMemo(
    () => ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "FREE SIZE"],
    []
  );

  function sortDimensionValues(dimName: string, values: string[]): string[] {
    if (/size/i.test(dimName)) {
      return [...values].sort((a, b) => {
        const idxA = SIZE_ORDER.indexOf(a.toUpperCase());
        const idxB = SIZE_ORDER.indexOf(b.toUpperCase());
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      });
    }
    return values;
  }

  // Extract secondary attribute dimensions (e.g. Size, Storage, RAM)
  const attributeDimensions = useMemo(() => {
    const dimMap = new Map<string, Set<string>>();

    // Check variant attributes and direct size property
    variants.forEach((v) => {
      if (v.size?.trim()) {
        if (!dimMap.has("Size")) dimMap.set("Size", new Set());
        v.size.split(/[,/|]+/).forEach((s) => {
          const trimmed = s.trim();
          if (trimmed) dimMap.get("Size")!.add(trimmed);
        });
      }
      if (v.attributes && typeof v.attributes === "object") {
        Object.entries(v.attributes).forEach(([key, val]) => {
          if (!val) return;
          const normalizedKey = key.trim();
          if (!dimMap.has(normalizedKey)) dimMap.set(normalizedKey, new Set());
          String(val)
            .split(/[,/|]+/)
            .forEach((s) => {
              const trimmed = s.trim();
              if (trimmed) dimMap.get(normalizedKey)!.add(trimmed);
            });
        });
      }
    });

    // Also check product specifications for Size, Storage, RAM if not already in variants
    if (product.specifications && typeof product.specifications === "object") {
      Object.entries(product.specifications).forEach(([key, val]) => {
        if (/^(size|storage|capacity|ram)/i.test(key) && typeof val === "string") {
          const normalizedKey = key.trim();
          if (!dimMap.has(normalizedKey)) {
            const values = val.split(/[,/|]+/).map((s) => s.trim()).filter(Boolean);
            if (values.length > 0) {
              dimMap.set(normalizedKey, new Set(values));
            }
          }
        }
      });
    }

    return Array.from(dimMap.entries()).map(([name, valSet]) => ({
      name,
      values: sortDimensionValues(name, Array.from(valSet)),
    }));
  }, [variants, product.specifications, SIZE_ORDER]);

  // Group variants by unique color to avoid duplicate swatches when variants have multiple sizes
  const colorGroups = useMemo(() => {
    if (!variants.length) return [];
    const map = new Map<
      string,
      {
        colorName: string;
        colorCode?: string;
        image?: string;
        variants: ProductVariant[];
        totalStock: number;
        isHot: boolean;
        minPrice: number;
      }
    >();

    variants.forEach((v) => {
      const colorKey = (v.color?.trim() || v.title?.trim() || "Standard").toLowerCase();
      const existing = map.get(colorKey);
      const stock = typeof v.stock === "number" ? v.stock : 0;
      const isHot = Boolean(v.isHot || v.badge === "hot" || v.attributes?.hot === "true");
      const price = typeof v.price === "number" ? v.price : product.price;

      if (!existing) {
        map.set(colorKey, {
          colorName: v.color?.trim() || v.title?.trim() || "Standard",
          colorCode: v.colorCode,
          image: v.image,
          variants: [v],
          totalStock: stock,
          isHot,
          minPrice: price,
        });
      } else {
        existing.variants.push(v);
        existing.totalStock += stock;
        if (isHot) existing.isHot = true;
        if (price < existing.minPrice) existing.minPrice = price;
        if (!existing.image && v.image) {
          existing.image = v.image;
        }
        if (!existing.colorCode && v.colorCode) {
          existing.colorCode = v.colorCode;
        }
      }
    });

    return Array.from(map.values());
  }, [variants, product.price]);

  const hasMultipleColors = useMemo(() => {
    if (colorGroups.length === 0) return false;
    if (colorGroups.length > 1) return true;
    const single = colorGroups[0];
    return single.colorName.toLowerCase() !== "standard" && Boolean(single.colorCode || single.image);
  }, [colorGroups]);

  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (initialVariant?.attributes) {
      Object.assign(initial, initialVariant.attributes);
    }
    return initial;
  });

  function handleSelectColor(colorGroup: (typeof colorGroups)[number]) {
    const currentSize =
      selectedAttributes["Size"] ||
      selectedAttributes["size"] ||
      selectedVariant?.size ||
      selectedVariant?.attributes?.["Size"] ||
      selectedVariant?.attributes?.["size"];

    let target = colorGroup.variants.find((v) => {
      const vSizes = (v.size || v.attributes?.["Size"] || v.attributes?.["size"] || "")
        .split(/[,/|]+/)
        .map((s) => s.trim().toLowerCase());
      return currentSize && vSizes.includes(currentSize.toLowerCase());
    });

    if (!target) {
      target = colorGroup.variants.find((v) => (v.stock ?? 0) > 0) || colorGroup.variants[0];
    }

    if (target) {
      handleSelectVariant(target);
      if (target.attributes) {
        setSelectedAttributes((prev) => ({ ...prev, ...target.attributes }));
      }
    }
  }

  function handleSelectAttribute(dimensionName: string, value: string) {
    const nextAttrs = { ...selectedAttributes, [dimensionName]: value };
    setSelectedAttributes(nextAttrs);

    // Try finding a variant that matches the currently selected color family AND this attribute value
    const currentColor = (
      selectedVariant?.color ||
      selectedVariant?.title ||
      ""
    ).trim().toLowerCase();

    const matchingVariant = variants.find((v) => {
      const vColor = (v.color || v.title || "").trim().toLowerCase();
      const colorMatch = currentColor ? vColor === currentColor : true;
      const attrVals = (v.attributes?.[dimensionName] || "")
        .split(/[,/|]+/)
        .map((s) => s.trim().toLowerCase());
      const sizeVals =
        dimensionName.toLowerCase() === "size" && v.size
          ? v.size.split(/[,/|]+/).map((s) => s.trim().toLowerCase())
          : [];
      const attrMatch =
        attrVals.includes(value.toLowerCase()) || sizeVals.includes(value.toLowerCase());
      return colorMatch && attrMatch;
    });

    if (matchingVariant) {
      handleSelectVariant(matchingVariant);
    } else {
      const anyMatching = variants.find((v) => {
        const attrVals = (v.attributes?.[dimensionName] || "")
          .split(/[,/|]+/)
          .map((s) => s.trim().toLowerCase());
        const sizeVals =
          dimensionName.toLowerCase() === "size" && v.size
            ? v.size.split(/[,/|]+/).map((s) => s.trim().toLowerCase())
            : [];
        return attrVals.includes(value.toLowerCase()) || sizeVals.includes(value.toLowerCase());
      });
      if (anyMatching) {
        handleSelectVariant(anyMatching);
      } else if (selectedVariant) {
        setSelectedVariant({
          ...selectedVariant,
          attributes: {
            ...(selectedVariant.attributes || {}),
            [dimensionName]: value,
          },
        });
      }
    }
  }

  // Sync if URL search parameter changes
  useEffect(() => {
    if (initialVariant && initialVariant.id !== selectedVariant?.id) {
      setSelectedVariant(initialVariant);
      if (initialVariant.image) {
        setActiveImage(initialVariant.image);
      }
    }
  }, [initialVariant]);

  // Combine product.images and any unique variant sub-URLs in a stable order
  const allImages = useMemo(() => {
    const set = new Set<string>();
    // Base images
    (product.images || []).forEach((img) => img && set.add(img));
    // Variant images
    variants.forEach((v) => {
      if (v.image) set.add(v.image);
      if (v.images && Array.isArray(v.images)) {
        v.images.forEach((img) => img && set.add(img));
      }
    });
    return Array.from(set);
  }, [product.images, variants]);

  const [activeImage, setActiveImage] = useState<string>(
    selectedVariant?.image || product.images[0] || ""
  );

  const findVariantForImage = (image: string): ProductVariant | undefined => {
    if (!variants.length) return undefined;
    const direct = variants.find(
      (v) => v.image === image || (Array.isArray(v.images) && v.images.includes(image))
    );
    if (direct) return direct;

    if (product.images.includes(image)) {
      const baseVariant = variants.find((v) => v.image && product.images.includes(v.image)) || variants[0];
      return baseVariant;
    }

    return undefined;
  };

  function handleSelectImage(image: string) {
    setActiveImage(image);
    const matched = findVariantForImage(image);
    if (matched && matched.id !== selectedVariant?.id) {
      setSelectedVariant(matched);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("variant", matched.id);
          return next;
        },
        { replace: true }
      );
    }
  }

  function handleSelectVariant(variant: ProductVariant) {
    setSelectedVariant(variant);
    if (variant.image) {
      setActiveImage(variant.image);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("variant", variant.id);
        return next;
      },
      { replace: true }
    );
  }

  function handleShare() {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Product link copied to clipboard");
    }
  }

  const aboutPoints = useMemo(() => {
    const text = product.description?.trim() || "";
    if (!text) return [];

    const lines = text
      .split(/\r?\n+/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length > 1) {
      return lines.map((l) => l.replace(/^[•\-\*]\s*/, ""));
    }

    const sentences = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 20);

    if (sentences.length > 1) {
      return sentences.map((s) => s.replace(/^[•\-\*]\s*/, ""));
    }

    return [text];
  }, [product.description]);

  // Determine the effective variant matching the currently displayed image or hover state
  const currentEffectiveVariant = useMemo(() => {
    if (hoveredVariant) return hoveredVariant;
    if (activeImage) {
      const match = findVariantForImage(activeImage);
      if (match) return match;
    }
    return selectedVariant || variants[0] || null;
  }, [hoveredVariant, activeImage, selectedVariant, variants, product.images]);

  const effectiveVariant = currentEffectiveVariant || selectedVariant;
  const effectivePrice = effectiveVariant ? effectiveVariant.price : product.price;
  const effectiveMrp = effectiveVariant?.mrp ?? product.mrp;
  const effectiveDiscount =
    effectiveMrp > effectivePrice
      ? Math.round(((effectiveMrp - effectivePrice) / effectiveMrp) * 100)
      : product.discount;

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-slate-900 sm:p-6">
      {product.status === "PENDING" && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <p className="font-bold">Preview Mode: Pending Admin Approval</p>
            <p className="text-xs text-amber-800 dark:text-amber-300">
              This customer marketplace listing is currently under review by our administrators and is hidden from public searches, category pages, and promotional banners.
            </p>
          </div>
        </div>
      )}
      {product.status === "REJECTED" && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-red-900 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="text-sm">
            <p className="font-bold">Preview Mode: Listing Rejected</p>
            <p className="text-xs text-red-800 dark:text-red-300">
              Rejection reason: <strong>{product.rejectionReason || "Listing needs revision."}</strong>. You can edit and resubmit this listing from your dashboard.
            </p>
          </div>
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,1.1fr)_280px] xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.85fr)_300px] lg:gap-8">
        {/* Left Column: Image Gallery with Sub-URLs */}
        <ProductGallery
          images={allImages}
          title={product.title}
          activeImage={activeImage}
          onSelectImage={handleSelectImage}
        />

        {/* Middle Column: Details, Price, and Color Variants */}
        <div className="lg:border-l lg:border-slate-100 lg:pl-6 dark:lg:border-white/10">
          <h1 className="text-xl font-bold leading-snug text-[#073B70] dark:text-white sm:text-2xl">
            {product.title}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <p className="text-sm text-[#087DB5]">
              Brand:{" "}
              <Link
                href={`/search?q=${encodeURIComponent(product.brand)}`}
                className="font-semibold hover:text-[#073B70] hover:underline dark:hover:text-sky-300"
              >
                {product.brand}
              </Link>
            </p>
            {isVendorProduct ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-0.5 text-xs font-semibold text-[#073B70] dark:border-sky-500/30 dark:bg-sky-950/40 dark:text-sky-200">
                <Store className="h-3 w-3 text-[#087DB5]" />
                <span>Sold by:</span>
                <span className="font-bold text-[#073B70] dark:text-white">
                  {product.seller || "Verified Vendor"}
                </span>
              </div>
            ) : isCustomerListing && product.sellerPhone ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-0.5 text-xs font-semibold text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-200">
                <Phone className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                <span>Seller:</span>
                <a
                  href={`tel:${product.sellerPhone}`}
                  className="font-bold text-emerald-700 underline hover:text-emerald-950 dark:text-emerald-300 dark:hover:text-white"
                >
                  {product.sellerPhone}
                </a>
              </div>
            ) : product.seller ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-0.5 text-xs font-semibold text-slate-700 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300">
                <ShieldCheck className="h-3 w-3 text-[#00B7B0]" />
                <span>Sold by:</span>
                <span className="font-bold text-[#073B70] dark:text-white">{product.seller}</span>
              </div>
            ) : null}
          </div>
          {product.rating >= 4.3 && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#073B70] to-[#087DB5] px-2.5 py-0.5 text-xs font-bold text-white shadow-xs">
                MOVA Guarantee
              </span>
            </div>
          )}

          {/* Website-Exclusive Dynamic Price Box */}
          <div className="mt-4 rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50/60 via-white to-sky-50/40 p-3.5 dark:border-amber-500/20 dark:from-slate-800/80 dark:to-slate-800/40">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#FF8A00]">
                Website-Exclusive Price
              </span>
              {effectiveDiscount > 0 && (
                <span className="inline-flex items-center rounded-full bg-[#FF8A00] px-2.5 py-0.5 text-xs font-bold text-white shadow-xs">
                  SAVE {effectiveDiscount}%
                </span>
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-baseline gap-2.5">
              <span className="text-3xl font-bold text-[#073B70] dark:text-white">
                {formatPrice(effectivePrice)}
              </span>
              {effectiveMrp > effectivePrice && (
                <span className="text-sm font-medium text-slate-400 line-through">
                  {formatPrice(effectiveMrp)}
                </span>
              )}
            </div>

            {effectiveMrp > effectivePrice && (
              <p className="mt-1 text-xs font-semibold text-[#00B7B0]">
                Better deals when you order online: save {formatPrice(effectiveMrp - effectivePrice)}!
              </p>
            )}

            {effectiveVariant?.sku && (
              <p className="mt-1.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                SKU: <span className="font-semibold text-slate-700 dark:text-slate-200">{effectiveVariant.sku}</span>
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Cash on delivery available across Ethiopia
            </p>
            {product.isPrime && <PrimeBadge className="mt-2" />}
          </div>

          {/* Variants (Color, Size, Storage, etc.) */}
          {variants.length > 0 && (
            <div className="mt-5 border-b border-slate-200 pb-5 dark:border-white/10 space-y-4">
              {/* Color Swatches Grid (Shown when multiple colors exist or explicit color styling is present) */}
              {hasMultipleColors && (
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-900 dark:text-white sm:text-base">
                      Color:{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {hoveredVariant?.color ||
                          hoveredVariant?.title ||
                          effectiveVariant?.color ||
                          effectiveVariant?.title ||
                          selectedVariant?.color ||
                          selectedVariant?.title ||
                          "Select a color"}
                      </span>
                    </p>
                    {effectiveVariant?.stock !== undefined && (
                      <span
                        className={cn(
                          "text-xs font-semibold",
                          effectiveVariant.stock > 5
                            ? "text-amazon-green"
                            : effectiveVariant.stock > 0
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-red-600 dark:text-red-400"
                        )}
                      >
                        {effectiveVariant.stock > 5
                          ? "In stock"
                          : effectiveVariant.stock > 0
                          ? `Only ${effectiveVariant.stock} left in stock`
                          : "Out of stock"}
                      </span>
                    )}
                  </div>

                  {/* AliExpress-Style Square Color Swatches Grid */}
                  <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                    {colorGroups.map((group) => {
                      const currentColor = (
                        effectiveVariant?.color ||
                        selectedVariant?.color ||
                        ""
                      )
                        .trim()
                        .toLowerCase();
                      const isSelected = currentColor === group.colorName.toLowerCase();
                      const isOutOfStock = group.totalStock <= 0;
                      const label = group.colorName;
                      const swatchKey = `swatch-${group.colorName}`;

                      // Variant to preview on hover
                      const currentSize =
                        selectedAttributes["Size"] ||
                        selectedAttributes["size"] ||
                        selectedVariant?.size ||
                        selectedVariant?.attributes?.["Size"] ||
                        selectedVariant?.attributes?.["size"];
                      const previewVariant =
                        group.variants.find((v) => {
                          const vSizes = (v.size || v.attributes?.["Size"] || v.attributes?.["size"] || "")
                            .split(/[,/|]+/)
                            .map((s) => s.trim().toLowerCase());
                          return currentSize && vSizes.includes(currentSize.toLowerCase());
                        }) || group.variants[0];

                      return (
                        <button
                          key={group.colorName}
                          type="button"
                          onClick={() => handleSelectColor(group)}
                          onMouseEnter={() => {
                            if (previewVariant) {
                              setHoveredVariant(previewVariant);
                              if (previewVariant.image) setActiveImage(previewVariant.image);
                            }
                          }}
                          onMouseLeave={() => {
                            setHoveredVariant(null);
                            const restoreImg = selectedVariant?.image || product.images[0];
                            if (restoreImg) setActiveImage(restoreImg);
                          }}
                          className={cn(
                            "group relative flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-all focus:outline-none",
                            isSelected
                              ? "border-2 border-[#073B70] bg-[#073B70]/5 shadow-xs dark:border-white dark:bg-slate-800 ring-2 ring-[#087DB5]/60"
                              : "border border-slate-200/90 bg-neutral-50 hover:border-[#087DB5] dark:border-white/10 dark:bg-slate-800/60 dark:hover:border-white/60",
                            isOutOfStock && "cursor-not-allowed opacity-40"
                          )}
                          title={`${label}${group.minPrice ? ` - from ${formatPrice(group.minPrice)}` : ""}${
                            isOutOfStock ? " (Out of stock)" : ""
                          }`}
                        >
                          {/* Swatch Image / Solid Color Box */}
                          <div className="relative h-full w-full overflow-hidden p-1">
                            {group.image && !swatchErrors[swatchKey] ? (
                              <Image
                                src={group.image}
                                alt={label}
                                fill
                                sizes="64px"
                                className="object-contain p-0.5"
                                onError={() =>
                                  setSwatchErrors((prev) => ({ ...prev, [swatchKey]: true }))
                                }
                              />
                            ) : (
                              <div
                                className="h-full w-full rounded-md"
                                style={{ backgroundColor: group.colorCode || "#000" }}
                              />
                            )}
                          </div>

                          {/* Hot Flame Icon Badge */}
                          {group.isHot && (
                            <span
                              className="absolute -right-2 -top-2.5 z-10 pointer-events-none drop-shadow-xs"
                              title="Popular Choice"
                            >
                              <Flame className="h-4 w-4 fill-[#FF8A00] text-[#FF8A00]" />
                            </span>
                          )}

                          {/* Out of Stock Diagonal Slash */}
                          {isOutOfStock && (
                            <span className="pointer-events-none absolute inset-0 m-auto h-[1.5px] w-[135%] -rotate-45 bg-slate-400 dark:bg-slate-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Attribute Dimension Pills (e.g. Size: S, M, L or Storage: 128GB, 256GB) */}
              {attributeDimensions.map(({ name, values }) => {
                const currentVal =
                  selectedAttributes[name] ||
                  selectedVariant?.attributes?.[name] ||
                  values[0];

                return (
                  <div key={name} className="border-t border-slate-100 pt-3 dark:border-white/5">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-slate-900 dark:text-white capitalize">
                        {name}:{" "}
                        <span className="font-bold text-[#073B70] dark:text-sky-300">
                          {currentVal}
                        </span>
                      </p>
                      {/size/i.test(name) && (
                        <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                          Standard Fit
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {values.map((val) => {
                        const isSelected = (currentVal || "").toLowerCase() === val.toLowerCase();
                        // Check stock of this specific size in the currently active color family
                        const currentColor = (
                          effectiveVariant?.color ||
                          selectedVariant?.color ||
                          ""
                        )
                          .trim()
                          .toLowerCase();
                        const variantForSize = variants.find((v) => {
                          const vColor = (v.color || v.title || "").trim().toLowerCase();
                          const colorMatch = currentColor ? vColor === currentColor : true;
                          const attrMatch = (v.attributes?.[name] || "").toLowerCase() === val.toLowerCase();
                          const sizeMatch = name.toLowerCase() === "size" && (v.size || "").toLowerCase() === val.toLowerCase();
                          return colorMatch && (attrMatch || sizeMatch);
                        });
                        const isSizeOutOfStock = variantForSize
                          ? variantForSize.stock !== undefined && variantForSize.stock <= 0
                          : false;

                        return (
                          <button
                            key={val}
                            type="button"
                            onClick={() => handleSelectAttribute(name, val)}
                            className={cn(
                              "relative min-w-12 h-10 sm:h-9 px-3.5 rounded-lg border text-xs font-bold uppercase transition focus:outline-none flex items-center justify-center gap-1 touch-manipulation",
                              isSelected
                                ? "border-[#087DB5] bg-sky-50 text-[#073B70] ring-2 ring-[#087DB5]/30 font-bold dark:bg-sky-950/40 dark:text-sky-200"
                                : "border-slate-300 bg-white text-slate-700 hover:border-[#087DB5] dark:border-white/15 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-white/40",
                              isSizeOutOfStock && "opacity-45 cursor-not-allowed bg-slate-50 dark:bg-slate-900"
                            )}
                            title={`${name} ${val}${isSizeOutOfStock ? " (Out of stock for this color)" : ""}`}
                          >
                            <span>{val}</span>
                            {isSizeOutOfStock && (
                              <span className="pointer-events-none absolute inset-0 m-auto h-[1.5px] w-[115%] -rotate-25 bg-red-400 dark:bg-red-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* MOVA Market Sticker Trust Assurance */}
          <div className="mt-5 rounded-xl border border-teal-200/60 bg-gradient-to-br from-teal-50/40 via-white to-sky-50/30 p-3.5 space-y-2 text-xs sm:text-sm text-slate-700 dark:border-teal-500/20 dark:from-slate-800/80 dark:to-slate-800/40 dark:text-slate-300">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00B7B0]/15 text-[#00B7B0]">
                <BadgeCheck className="h-4 w-4" />
              </div>
              <div>
                <span className="font-bold text-[#073B70] dark:text-white">Quality Products: </span>
                <span>We Don&apos;t Just Sell, We Guarantee Quality!</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00B7B0]/15 text-[#00B7B0]">
                <Truck className="h-4 w-4" />
              </div>
              <div>
                <span className="font-bold text-[#073B70] dark:text-white">Fast & Reliable Delivery: </span>
                <span>Available nationwide across Ethiopia</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00B7B0]/15 text-[#00B7B0]">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div>
                <span className="font-bold text-[#073B70] dark:text-white">Secure Payments: </span>
                <span>Cash on Delivery (COD) supported with safe transactions</span>
              </div>
            </div>
            {product.warranty && (
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00B7B0]/15 text-[#00B7B0]">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <span className="font-bold text-[#073B70] dark:text-white">Warranty: </span>
                  <span>{product.warranty}</span>
                </div>
              </div>
            )}
          </div>

          {/* About this item (Quick Highlights) */}
          {aboutPoints.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-white/10">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                About this item
              </h3>
              <ul className="mt-2.5 space-y-2 pl-4 text-xs leading-relaxed text-slate-700 list-disc marker:text-slate-400 dark:text-slate-300 sm:text-sm">
                {aboutPoints.slice(0, 5).map((bullet, idx) => (
                  <li key={idx} className="leading-snug">
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Mobile Actions */}
          <div className="mt-4 lg:hidden">
            <ProductActions product={product} selectedVariant={effectiveVariant} compact />
          </div>

          <button
            type="button"
            onClick={handleShare}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#087DB5] hover:text-[#073B70] transition-colors"
          >
            <Share2 className="h-3.5 w-3.5" />
            Share
          </button>
        </div>

        {/* Right Column (Desktop Purchase Box) */}
        <div className="hidden space-y-3 lg:block">
          <ProductActions product={product} selectedVariant={effectiveVariant} />
        </div>
      </div>
    </section>
  );
}
